/**
 * `<WorkspaceSwitcher/>` — IA Phase 2 chunks IA-2.2 + IA-2.4.
 *
 * The persistent top-left pill that names the active workspace + lets
 * the user switch teams without a click-back-to-Home detour. Per the
 * IA-redesign research at `26664e8` and the implementation plan
 * `docs/design/ia-phase-2-implementation-plan.md` §3 IA-2.2.
 *
 * IA-2.4 extension (this file): the `onSelect` click handler preserves
 * the user's sub-route intent when switching workspaces — see the
 * `resolveWorkspaceClickDestination` decision table below.
 *
 * Focal-component discipline (per the engineering-lead saved memory
 * `feedback_ui_section_focal_component.md`):
 *
 *   - **AppShell top-left chrome.** This pill is the focal element.
 *     The Tarii logo (left of the pill) is supporting; the module
 *     switcher (`Schedule ▾`, right of the pill) is supporting nav.
 *     The pill carries the most weight: full role chip, full team
 *     name, the "where I am right now" anchor.
 *   - **Inside the dropdown.** The active-workspace row at the top is
 *     the focal element — selected state + role chip prominent. The
 *     "Your teams" header is supporting context (smaller, muted). The
 *     "+ Create new team" + "+ Join with invite code" rows are
 *     tertiary affordances at the bottom.
 *   - **Per row.** The workspace name is the focal text (largest); the
 *     role chip is secondary; the plan tier is intentionally NOT
 *     surfaced inline (footnote-quality at v0.1, defer until the
 *     §4.6 free-tier upsell-on-create flow asks for it).
 *
 * Brand-pillar discipline (per `feedback_brand_pillars.md`):
 *
 *   - Worker-friendly: the pill renders identically for members and
 *     leads; only the role chip's tone changes. No "audit log" or
 *     "rank" framing in the dropdown copy.
 *   - Trustable: the role chip is honest signage about which workspace
 *     the user is in. No marketing puffery on the empty-state /
 *     create-team copy.
 *   - Plural-"we" voice (per `feedback_brand_organization_shape.md`):
 *     the "+ Create new team" item reads as a neutral affordance, not
 *     "I'll set this up for you" founder voice.
 *
 * Founder defaults applied (per implementation plan §4):
 *
 *   - §4.1 ordering — server-side recency from IA-2.1; consume in
 *     array order; never re-sort client-side.
 *   - §4.2 cycle pages — the switcher mounts as today; the breadcrumb
 *     team-segment removal audit (IA-2.4) confirmed cycle-page
 *     breadcrumbs (`CycleRoutes.tsx` + `CyclePage.tsx`) already render
 *     `Home › Cycle <short-id>` with no team segment, so default (c)
 *     is already in place. A regression test in
 *     `WorkspaceSwitcher.test.tsx` asserts the cycle breadcrumb feed
 *     never carries a team-name segment.
 *   - §4.3 single-team threshold — render always (≥1). Build the
 *     `MIN_WORKSPACES_TO_RENDER_SWITCHER` constant; default to 1.
 *   - §4.6 single-team-user migration — default-on, render for all.
 *   - §4.7 feature-flag rollout — default-on with the
 *     `tarii.workspace_switcher_disabled` localStorage kill-switch
 *     mounted in `AppShell.tsx`.
 */
import { ChevronsUpDown, Eye, Plus, Shield, Users } from "lucide-react";
import { useCallback, useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { type AdminAllTeamsRow, useAdminTeams } from "../api/useAdminTeams";
import { type ObservedTeam, useObservedTeam } from "../api/useObservedTeam";
import { type WorkspaceRow, useWorkspaces } from "../api/useWorkspaces";
import { AuthContext } from "../lib/AuthContext";
import { getActiveWorkspaceId, setActiveWorkspaceId } from "../lib/activeWorkspace";
import { cn } from "../lib/cn";
import { useDemoLocked } from "../lib/demoLock";
import { type TeamKind, resolveTeamHomeRoute } from "../lib/teamApps";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Skeleton,
  Tooltip,
} from "./ui";

/** Threshold for rendering the switcher. Founder default per §4.3 is
 *  1 (render always — the dropdown's create / join affordances make it
 *  valuable even with one workspace). Flip to 2 to opt into the
 *  progressive-disclosure alternative if a real lead asks. */
export const MIN_WORKSPACES_TO_RENDER_SWITCHER = 1;

/**
 * IA-2.4 — sub-route preservation on workspace click.
 *
 * Pure function: given the current URL (path + search) and the target
 * workspace id, return either a destination URL to `navigate(...)` to,
 * or `null` to indicate "stay on the current path; the only thing that
 * changes is the active workspace id" (workspace-agnostic case, e.g.
 * `/me/audit`).
 *
 * Decision table per implementation plan §3 IA-2.4:
 *
 *   - `/teams/<active>/setup/<sub>` → `/teams/<target>/setup/<sub>` —
 *     preserve the Setup sub-route so a lead in StationsPage on team A
 *     who clicks team B lands on StationsPage for team B (not on
 *     TeamPage cycle list, which would discard their context).
 *   - `/teams/<active>` (with optional `?tab=...`) → `/teams/<target>`
 *     with the same `?tab=...`, so a lead inspecting the Members tab
 *     on team A and switching to team B lands on team B's Members tab.
 *   - `/cycles/...` → `/teams/<target>` — cycles are per-workspace; the
 *     user is leaving the cycle, so default to TeamPage (the cycle
 *     belongs to a different workspace and we don't want to silently
 *     resolve a cross-tenant cycle id).
 *   - `/teams/<active>/map/...` → `resolveTeamHomeRoute(targetKind, targetTeamId)` —
 *     task-mode routes are kind-specific; when switching teams we always
 *     land on the target team's home (scheduling or tasks), never carry
 *     over the map sub-route to a scheduling team.
 *   - any other path (`/me/audit`, `/me/insights`, `/modules/<name>`,
 *     `/settings/profile`, etc.) → null — these are workspace-agnostic
 *     surfaces; the active workspace state updates but the visible URL
 *     stays the same. The switcher pill re-renders with the new
 *     workspace name + role chip.
 *
 * The `currentPath` arg is the pathname only (no search / hash); the
 * `currentSearch` arg is the raw `location.search` string including the
 * leading `?`. Returning a string with the `?<search>` suffix preserves
 * search params on the destination (the TeamPage `?tab=members` case).
 *
 * `targetKind` is the `team_kind` of the target workspace (resolved by
 * the caller from the workspaces array). Defaults to `'scheduling'` for
 * backward-compat when not provided.
 */
export function resolveWorkspaceClickDestination(
  currentPath: string,
  currentSearch: string,
  targetTeamId: string,
  targetKind: TeamKind = "scheduling",
): string | null {
  // Match `/teams/<active>/setup/<sub>` first — must be more specific
  // than the bare `/teams/<active>` match below.
  const setupMatch = currentPath.match(/^\/teams\/[0-9a-f-]+\/setup(\/.*)?$/i);
  if (setupMatch) {
    if (targetKind === "tasks") {
      // A tasks team has no Setup sub-routes; land on its map home.
      return resolveTeamHomeRoute(targetKind, targetTeamId);
    }
    const subPath = setupMatch[1] ?? "";
    return `/teams/${targetTeamId}/setup${subPath}`;
  }

  // Match `/teams/<active>/map/...` (tasks-mode routes) — always land
  // on the target team's app home (kind-specific). A scheduling team
  // has no map surface; a tasks team's map is its home.
  if (/^\/teams\/[0-9a-f-]+\/map(?:\/.*)?$/i.test(currentPath)) {
    return resolveTeamHomeRoute(targetKind, targetTeamId);
  }

  // Match `/teams/<active>` (optionally with a trailing slash). The
  // `?tab=...` search param rides along verbatim so a lead inspecting
  // a non-default tab on team A keeps the same tab on team B.
  // For a tasks team, always land on the map home instead.
  if (/^\/teams\/[0-9a-f-]+\/?$/i.test(currentPath)) {
    if (targetKind === "tasks") {
      return resolveTeamHomeRoute(targetKind, targetTeamId);
    }
    return `/teams/${targetTeamId}${currentSearch || ""}`;
  }

  // Match any cycle route — `/cycles/<id>`, `/cycles/<id>/<tab>`,
  // `/cycles/<id>/submit`, `/cycles/<id>/blocks`. Cycles are scoped to
  // their owning workspace; clicking a different workspace is leaving
  // the cycle context entirely, so route to the new workspace's
  // app home as the safe default.
  if (/^\/cycles\//i.test(currentPath)) {
    return resolveTeamHomeRoute(targetKind, targetTeamId);
  }

  // Workspace-agnostic surface: `/me/...`, `/modules/<name>`,
  // `/settings/profile`, `/legal/...`, `/`, `/onboarding`, etc.
  // Return null so the caller knows to skip the navigate call. The
  // active workspace id is still updated so the switcher pill shows
  // the new active workspace.
  return null;
}

/**
 * Inner menu list — extracted so tests can exercise the rows directly
 * without depending on Radix's pointer-event-driven open-on-click
 * (happy-dom doesn't simulate it; see `theme.test.tsx` precedent for
 * the same workaround on `<ThemePicker/>`).
 */
export interface WorkspaceMenuListProps {
  workspaces: ReadonlyArray<WorkspaceRow>;
  /** Null while an admin observes a team of someone else's (or has no
   *  team of their own): no own row is highlighted. */
  active: WorkspaceRow | null;
  onSelect: (workspace: WorkspaceRow) => void;
  onCreate: () => void;
  onJoin: () => void;
  /** Issue #14 — when true, the "+ Create new team" row is greyed +
   *  non-clickable, with `createDisabledReason` shown as a tooltip.
   *  Driven by the `max_teams` / `current_team_count` pair from
   *  `/api/account/me`. Defaults to enabled when omitted (back-compat
   *  for any caller that doesn't yet pass plan data). */
  createDisabled?: boolean;
  createDisabledReason?: string;
  /** #2060 — an admin account's *All teams (observer)* section. Omitted
   *  (the default) for everyone else: the menu is then exactly as before. */
  observer?: ObserverSectionProps | undefined;
}

/** #2060 — the switcher's *All teams (observer)* section. */
export interface ObserverSectionProps {
  /** Every team on the platform (`GET /api/admin/teams`); the caller's own
   *  teams are dropped here, not by the caller. */
  teams: ReadonlyArray<AdminAllTeamsRow> | undefined;
  loading?: boolean;
  /** The observed team on screen, highlighted. */
  activeTeamId?: string | null;
  showDemo: boolean;
  onShowDemoChange: (next: boolean) => void;
  onSelect: (team: AdminAllTeamsRow) => void;
}

/** The All-teams rows: not the caller's own teams; demo teams only when
 *  asked for. Pure, for the tests. */
export function observerTeamRows(
  teams: ReadonlyArray<AdminAllTeamsRow>,
  ownTeamIds: ReadonlySet<string>,
  showDemo: boolean,
): { rows: AdminAllTeamsRow[]; hiddenDemo: number } {
  const others = teams.filter((t) => !ownTeamIds.has(t.team_id));
  const rows = showDemo ? others : others.filter((t) => !t.is_demo);
  return {
    rows: [...rows].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    ),
    hiddenDemo: others.length - rows.length,
  };
}

export function WorkspaceMenuList({
  workspaces,
  active,
  onSelect,
  onCreate,
  onJoin,
  createDisabled = false,
  createDisabledReason,
  observer,
}: WorkspaceMenuListProps) {
  const demoLocked = useDemoLocked();
  return (
    <div data-testid="workspace-switcher-menu" className="flex flex-col gap-1">
      {workspaces.length > 0 || !observer ? (
        <div className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-fg-faint">
          Your teams
        </div>
      ) : null}
      {workspaces.map((workspace) => {
        const isActive = active !== null && workspace.team_id === active.team_id;
        const isTasksKind = workspace.team_kind === "tasks";
        return (
          <button
            type="button"
            key={workspace.team_id}
            onClick={() => onSelect(workspace)}
            data-testid={`workspace-switcher-row-${workspace.team_id}`}
            data-team-kind={workspace.team_kind}
            className={cn(
              "relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none",
              "text-fg-muted transition-colors hover:bg-bg-subtle hover:text-fg",
              isActive ? "bg-bg-subtle" : "",
            )}
          >
            {/* B7.1 — each team shows its kind affordance.
                Active scheduling team: Shield (existing, accent).
                Active tasks team:      Map emoji (visual distinction).
                Inactive scheduling:    Users icon.
                Inactive tasks:         Map emoji (muted). */}
            {isActive ? (
              isTasksKind ? (
                <span
                  className="flex-shrink-0 text-[14px] leading-none"
                  aria-hidden="true"
                  data-testid={`kind-icon-tasks-${workspace.team_id}`}
                >
                  🗺
                </span>
              ) : (
                <Shield className="h-3.5 w-3.5 flex-shrink-0 text-accent-fg" aria-hidden="true" />
              )
            ) : isTasksKind ? (
              <span
                className="flex-shrink-0 text-[14px] leading-none opacity-60"
                aria-hidden="true"
                data-testid={`kind-icon-tasks-${workspace.team_id}`}
              >
                🗺
              </span>
            ) : (
              <Users className="h-3.5 w-3.5 flex-shrink-0 text-fg-subtle" aria-hidden="true" />
            )}
            <span
              className={cn(
                "flex-1 truncate text-left text-sm",
                isActive ? "font-semibold text-fg" : "font-medium text-fg",
              )}
            >
              {workspace.team_name}
            </span>
            {/* B7.1 — kind badge: scheduling teams show role chip only;
                tasks teams also show a "tasks" kind label so the user
                can distinguish mixed workspaces at a glance. */}
            {isTasksKind && (
              <Badge
                tone="neutral"
                className="px-1.5 py-0 text-[10px]"
                data-testid={`kind-badge-${workspace.team_id}`}
              >
                tasks
              </Badge>
            )}
            <Badge tone={roleTone(workspace.role)} className="px-1.5 py-0 text-[10px]">
              {roleLabel(workspace.role)}
            </Badge>
          </button>
        );
      })}
      {observer ? (
        <ObserverSection
          observer={observer}
          ownTeamIds={new Set(workspaces.map((w) => w.team_id))}
          divided={workspaces.length > 0}
        />
      ) : null}
      {/* #2824 - the demo creates and joins no team. */}
      {demoLocked ? null : (
        <>
          <div className="-mx-1 my-1 h-px bg-border" />
          <Tooltip content={createDisabled ? createDisabledReason : ""} side="right">
            <button
              type="button"
              onClick={createDisabled ? undefined : onCreate}
              disabled={createDisabled}
              aria-disabled={createDisabled || undefined}
              data-testid="workspace-switcher-create"
              data-disabled={createDisabled || undefined}
              className={cn(
                "relative flex select-none items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none transition-colors",
                createDisabled
                  ? "cursor-not-allowed text-fg-faint"
                  : "cursor-pointer text-fg-muted hover:bg-bg-subtle hover:text-fg",
              )}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span className="flex-1 text-left">Create a new team</span>
            </button>
          </Tooltip>
          <button
            type="button"
            onClick={onJoin}
            data-testid="workspace-switcher-join"
            className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-3 py-2 text-sm text-fg-muted outline-none transition-colors hover:bg-bg-subtle hover:text-fg"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            <span className="flex-1 text-left">Join with an invite code</span>
          </button>
        </>
      )}
    </div>
  );
}

/** #2060 — *All teams (observer)*: every other team on the platform, read
 *  only. Demo teams wait behind a toggle (remembered per browser). */
function ObserverSection({
  observer,
  ownTeamIds,
  divided,
}: {
  observer: ObserverSectionProps;
  ownTeamIds: ReadonlySet<string>;
  /** A rule above the section, under Your teams (none when there are none). */
  divided: boolean;
}) {
  const { rows, hiddenDemo } = observerTeamRows(
    observer.teams ?? [],
    ownTeamIds,
    observer.showDemo,
  );
  const demoTotal = (observer.teams ?? []).filter(
    (t) => t.is_demo && !ownTeamIds.has(t.team_id),
  ).length;
  return (
    <div className="flex flex-col gap-1" data-testid="workspace-switcher-observer">
      {divided ? <div className="-mx-1 my-1 h-px bg-border" /> : null}
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <span className="text-xs font-medium uppercase tracking-wide text-fg-faint">
          All teams (observer)
        </span>
      </div>
      {observer.loading ? (
        <Skeleton className="mx-3 h-6" />
      ) : rows.length === 0 ? (
        <p
          className="px-3 pb-1 text-xs text-fg-muted"
          data-testid="workspace-switcher-observer-empty"
        >
          {hiddenDemo > 0 ? "Only demo teams besides yours." : "No other teams."}
        </p>
      ) : (
        <div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
          {rows.map((team) => {
            const isActive = observer.activeTeamId === team.team_id;
            return (
              <button
                type="button"
                key={team.team_id}
                onClick={() => observer.onSelect(team)}
                data-testid={`workspace-switcher-observer-row-${team.team_id}`}
                data-team-kind={team.team_kind}
                className={cn(
                  "relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none",
                  "text-fg-muted transition-colors hover:bg-bg-subtle hover:text-fg",
                  isActive ? "bg-bg-subtle" : "",
                )}
              >
                <Eye
                  className={cn(
                    "h-3.5 w-3.5 flex-shrink-0",
                    isActive ? "text-accent-fg" : "text-fg-subtle",
                  )}
                  aria-hidden="true"
                />
                <span
                  className={cn(
                    "flex-1 truncate text-left text-sm text-fg",
                    isActive ? "font-semibold" : "font-medium",
                  )}
                >
                  {team.name}
                </span>
                {team.team_kind === "tasks" ? (
                  <Badge tone="neutral" className="px-1.5 py-0 text-[10px]">
                    tasks
                  </Badge>
                ) : null}
                {team.is_demo ? (
                  <Badge tone="neutral" className="px-1.5 py-0 text-[10px]">
                    demo
                  </Badge>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
      {demoTotal > 0 ? (
        <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs text-fg-muted">
          <input
            type="checkbox"
            checked={observer.showDemo}
            onChange={(e) => observer.onShowDemoChange(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-border"
            data-testid="workspace-switcher-show-demo"
          />
          Show demo teams ({demoTotal})
        </label>
      ) : null}
    </div>
  );
}

/** #2060 — the *Show demo teams* choice, per browser. */
const SHOW_DEMO_KEY = "tarii.switcher.show_demo_teams";

export function readShowDemoTeams(): boolean {
  try {
    return window.localStorage.getItem(SHOW_DEMO_KEY) === "1";
  } catch {
    return false;
  }
}

function writeShowDemoTeams(next: boolean): void {
  try {
    window.localStorage.setItem(SHOW_DEMO_KEY, next ? "1" : "0");
  } catch {
    // Blocked storage: the toggle still works for this visit.
  }
}

/** Role label rendered on the pill + each dropdown row. Honest signage
 *  per the brand pillar — the worker sees their role on each workspace
 *  without having to dig into Setup. */
function roleLabel(role: WorkspaceRow["role"]): string {
  if (role === "lead") return "Lead";
  if (role === "co_lead") return "Co-Lead";
  return "Member";
}

/** Lead / Co-Lead use the accent tone (default-weighted; co-leads have
 *  lead-equivalent capabilities per implementation plan §3 IA-2.2 +
 *  IA-redesign §7 row 7). Member uses the neutral tone — calm signage,
 *  not muted-out. */
function roleTone(role: WorkspaceRow["role"]): "accent" | "neutral" {
  return role === "member" ? "neutral" : "accent";
}

/** Issue #14 — derive the create-team gate from `/api/account/me`'s
 *  `max_teams` + `current_team_count` pair. `max_teams === null` (or
 *  undefined, for backward-compat with older API builds) → unlimited.
 *  Pure / unit-testable; no React. */
export function resolveCreateTeamGate(
  maxTeams: number | null | undefined,
  currentTeamCount: number,
  _plan: string,
): { disabled: boolean; reason: string } {
  if (maxTeams == null) {
    return { disabled: false, reason: "" };
  }
  if (currentTeamCount < maxTeams) {
    return { disabled: false, reason: "" };
  }
  const noun = maxTeams === 1 ? "team" : "teams";
  return {
    disabled: true,
    reason: `This account can hold ${maxTeams} ${noun}. Contact us to add more.`,
  };
}

export function WorkspaceSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const workspacesQuery = useWorkspaces();
  // Issue #14 — read AuthContext directly (not via `useAuth`) so the
  // switcher remains rendererable in test harnesses that mount it
  // without an AuthProvider wrapper. The cap data is non-critical
  // (backend is the safety net); falling back to null keeps the row
  // permissive in the no-provider case.
  const authCtx = useContext(AuthContext);
  const account = authCtx?.account ?? null;
  const workspaces = useMemo<WorkspaceRow[]>(
    () => workspacesQuery.data ?? [],
    [workspacesQuery.data],
  );
  // #2060 — an admin account also sees *All teams (observer)*; the pill
  // names the observed team while one is on screen. Everyone else: no
  // request, no change.
  const isAdmin = account?.is_admin === true;
  const adminTeams = useAdminTeams({ enabled: isAdmin });
  const observed: ObservedTeam | null = useObservedTeam();
  const [showDemo, setShowDemo] = useState<boolean>(readShowDemoTeams);

  // Active workspace resolution. The pill consumes whatever is in
  // localStorage if it's still in the membership list; otherwise the
  // most-recently-active row from the server-ordered array. We do NOT
  // write back to localStorage from here — the AppShell's first-mount
  // call (IA-2.3) owns the stale-clear. The switcher just reads.
  const activeId = getActiveWorkspaceId();
  const active: WorkspaceRow | null = useMemo(() => {
    if (workspaces.length === 0) return null;
    if (activeId) {
      const stored = workspaces.find((w) => w.team_id === activeId);
      if (stored) return stored;
    }
    return workspaces[0] ?? null;
  }, [workspaces, activeId]);

  const onSelect = useCallback(
    (workspace: WorkspaceRow) => {
      setActiveWorkspaceId(workspace.team_id);
      // IA-2.4 sub-route preservation + #753 team-kind routing.
      // The pure resolver decides where to navigate based on the
      // current URL shape AND the target team's kind — tasks teams
      // always land on their map home; scheduling teams preserve
      // Setup sub-routes / TeamPage tabs.
      const destination = resolveWorkspaceClickDestination(
        location.pathname,
        location.search,
        workspace.team_id,
        workspace.team_kind as TeamKind,
      );
      if (destination !== null) {
        navigate(destination);
      }
      // null → workspace-agnostic surface; the active workspace state
      // updates but the URL stays the same. The switcher pill
      // re-renders with the new workspace name on the next read.
    },
    [navigate, location.pathname, location.search],
  );

  const onSelectObserved = useCallback(
    (team: AdminAllTeamsRow) => {
      // The active workspace stays the caller's own team: observing is a
      // visit, read off the address, never a membership.
      const kind: TeamKind = team.team_kind === "tasks" ? "tasks" : "scheduling";
      navigate(
        resolveWorkspaceClickDestination(location.pathname, location.search, team.team_id, kind) ??
          resolveTeamHomeRoute(kind, team.team_id),
      );
    },
    [navigate, location.pathname, location.search],
  );
  const onShowDemoChange = useCallback((next: boolean) => {
    setShowDemo(next);
    writeShowDemoTeams(next);
  }, []);

  const onCreate = useCallback(() => {
    navigate("/onboarding");
  }, [navigate]);

  const onJoin = useCallback(() => {
    // The invite-accept flow lives at `/invite/:token`; without a
    // token we route to onboarding which carries the invite-code
    // entry path. Keeping this neutral so the dropdown doesn't deep-
    // link to a Pro-only paywall on first click.
    navigate("/onboarding");
  }, [navigate]);

  // Issue #14 — multi-team cap gate. The numbers come from
  // `/api/account/me` (loaded into the AuthContext on mount). When
  // `account` hasn't resolved yet the gate is permissive (don't grey
  // a button on a half-loaded chrome); the backend will still reject
  // the create call with the 402 envelope as the safety net.
  const createGate = useMemo(() => {
    if (!account) {
      return { disabled: false, reason: "" };
    }
    return resolveCreateTeamGate(
      account.max_teams ?? null,
      account.current_team_count ?? 0,
      account.plan,
    );
  }, [account]);

  // Issue 004 fix — three-branch render gate:
  //
  //   1. Loading (no data yet, no error) → render the skeleton pill so
  //      the slot occupies space. Without this, the AppShell chrome
  //      "blinks" the switcher in only after TanStack resolves the
  //      query, which on a slow / cold-start request was reported by
  //      the founder as "the switcher just isn't there" (UAT 2026-05-03).
  //   2. Error (terminal failure on /api/account/me/workspaces) →
  //      render the skeleton pill with an honest aria-label rather
  //      than vanishing silently. Trustable-pillar: a wary user reading
  //      the chrome can see Tarii is still trying, not that the feature
  //      "doesn't exist for me". Workspace 500 P0 (`fb295be`,
  //      TransparencyMode='summary' Pydantic mismatch) is the exact
  //      class of bug this guard now survives.
  //   3. Resolved + empty memberships (data is `[]` AND not loading
  //      AND not errored) → render nothing. Per IA-2.2 acceptance: a
  //      brand-new account on /onboarding sees no switcher.
  //
  // The kill-switch (`tarii.workspace_switcher_disabled`) is enforced
  // upstream in AppShell.tsx — by the time control reaches here, the
  // founder default §4.3 (render at ≥1 workspace) is the only gate left
  // for the resolved-non-empty path.
  const isResolved = !workspacesQuery.isLoading && !workspacesQuery.isError;
  if (workspacesQuery.isLoading || workspacesQuery.isError) {
    return (
      <output
        data-testid="workspace-switcher-skeleton"
        aria-label="Loading your team"
        className="flex items-center"
      >
        <Skeleton className="h-7 w-32" />
      </output>
    );
  }
  if (
    !isAdmin &&
    isResolved &&
    (workspaces.length < MIN_WORKSPACES_TO_RENDER_SWITCHER || !active)
  ) {
    return null;
  }
  if (!active && !isAdmin) {
    // Belt-and-braces: TS doesn't narrow `active` through the resolved
    // branch above, so guard explicitly. The skeleton is the safer
    // fallback than null (see comment block above).
    return (
      <output
        data-testid="workspace-switcher-skeleton"
        aria-label="Loading your team"
        className="flex items-center"
      >
        <Skeleton className="h-7 w-32" />
      </output>
    );
  }

  // #2060 — the pill: the observed team while one is on screen, else the
  // active own team, else (an admin with no team of their own) "All teams".
  const pillName = observed?.name ?? active?.team_name ?? "All teams";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          className="min-w-0 gap-2 pl-2.5 pr-2"
          data-testid="workspace-switcher-trigger"
          data-observing={observed ? "true" : undefined}
          aria-label={`Active workspace: ${pillName}`}
        >
          {observed ? (
            <Eye className="h-3.5 w-3.5 text-fg-muted" aria-hidden="true" />
          ) : (
            <Users className="h-3.5 w-3.5 text-fg-muted" aria-hidden="true" />
          )}
          <span className="max-w-[10rem] truncate text-sm font-medium text-fg">{pillName}</span>
          {/* #2048 — "Lead" is the default and says nothing on the pill;
              Co-Lead and Member still show. The menu rows keep every chip. */}
          {!observed && active && active.role !== "lead" ? (
            <Badge tone={roleTone(active.role)} className="px-1.5 py-0 text-[10px]">
              {roleLabel(active.role)}
            </Badge>
          ) : null}
          <ChevronsUpDown className="h-3.5 w-3.5 text-fg-subtle" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[16rem] max-w-[calc(100vw-2rem)] p-1">
        <WorkspaceMenuList
          workspaces={workspaces}
          active={observed ? null : active}
          onSelect={onSelect}
          onCreate={onCreate}
          onJoin={onJoin}
          createDisabled={createGate.disabled}
          createDisabledReason={createGate.reason}
          observer={
            isAdmin
              ? {
                  teams: adminTeams.data?.teams,
                  loading: adminTeams.isLoading,
                  activeTeamId: observed?.id ?? null,
                  showDemo,
                  onShowDemoChange,
                  onSelect: onSelectObserved,
                }
              : undefined
          }
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
